/* eslint-disable react-refresh/only-export-components */
import type {ReactElement} from 'react';
import {useCallback, useContext, useSyncExternalStore} from 'react';
import type {Component, Editor} from 'grapesjs';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {Icon} from '@Components/icon-v2/icon';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {getComponentStyleProperty, useStyleChangeSubscription} from '@EmailMakerReact/lib/email-maker-component-style-library';
import type {EmailMakerControl, EmailMakerControlProps} from '../../right-panel-registry.types';
import {createDescriptorControl} from '../create-descriptor-control';
import type {OptionListDescriptor, OptionListItem} from './option-list-control.types';
import {OPTION_LIST_KEY_SEPARATOR, OPTION_LIST_SELECTION_SEPARATOR} from './option-list-control.types';
import styles from './option-list-control.module.scss';

interface OptionListControlInnerProps extends EmailMakerControlProps {
  descriptor: OptionListDescriptor;
}

function itemProperty(item: OptionListItem, descriptor: OptionListDescriptor): string {
  return item.property ?? descriptor.property;
}

function itemKey(item: OptionListItem, descriptor: OptionListDescriptor): string {
  return `${itemProperty(item, descriptor)}${OPTION_LIST_KEY_SEPARATOR}${item.value}`;
}

function readItemValue(component: Component, item: OptionListItem, descriptor: OptionListDescriptor): string {
  if (descriptor.readValue) {
    return descriptor.readValue(component);
  }
  return getComponentStyleProperty(component, itemProperty(item, descriptor));
}

function isItemValueSetInModel(component: Component, item: OptionListItem, descriptor: OptionListDescriptor): boolean {
  const rawValue = readItemValue(component, item, descriptor);
  if (!rawValue) {
    return false;
  }
  return rawValue.split(' ').includes(item.value);
}

function buildModelSelection(component: Component, descriptor: OptionListDescriptor): Set<string> {
  const selectableItems = descriptor.list.filter((item) => item.selectable !== false);
  const selected = new Set(selectableItems.filter((item) => isItemValueSetInModel(component, item, descriptor)).map((item) => itemKey(item, descriptor)));
  if (selected.size > 0 || descriptor.toggleable) {
    return selected;
  }
  const defaultItem = selectableItems.find((item) => item.value === descriptor.defaults);
  if (defaultItem) {
    selected.add(itemKey(defaultItem, descriptor));
  }
  return selected;
}

function serializeSelection(selection: Set<string>): string {
  return Array.from(selection).sort().join(OPTION_LIST_SELECTION_SEPARATOR);
}

function deserializeSelection(serialized: string): Set<string> {
  return new Set(serialized === '' ? [] : serialized.split(OPTION_LIST_SELECTION_SEPARATOR));
}

function useModelSelection(editor: Editor | null, component: Component, descriptor: OptionListDescriptor): Set<string> {
  const subscribe = useStyleChangeSubscription(editor, component, descriptor.property);
  const readSelectionSnapshot = useCallback((): string => serializeSelection(buildModelSelection(component, descriptor)), [component, descriptor]);
  return deserializeSelection(useSyncExternalStore(subscribe, readSelectionSnapshot));
}

function renderLabel(item: OptionListItem): ReactElement | null {
  if (!item.label) {
    return null;
  }
  return <Text val={item.label} size={TextSize.MD} className={styles.itemLabel} />;
}

function OptionListControlInner({component, descriptor}: OptionListControlInnerProps): ReactElement {
  const {editor} = useContext(emailMakerContext);
  const selectedKeys = useModelSelection(editor, component, descriptor);

  function handleItemClick(item: OptionListItem): void {
    if (item.selectable === false || !editor) {
      return;
    }
    item.callback(component, editor);
  }

  function renderItem(item: OptionListItem): ReactElement {
    const isSelected = selectedKeys.has(itemKey(item, descriptor));
    const parentClasses = item.parentClasses ?? '';
    const resolvedName = window.i18next.t(item.name);

    return (
      <li className={`${styles.item} ${parentClasses}`} key={itemKey(item, descriptor)} title={resolvedName}>
        <button
          aria-label={resolvedName}
          aria-pressed={isSelected}
          className={styles.itemButton}
          onClick={(): void => {
            handleItemClick(item);
          }}
          type="button"
        >
          <Icon icon={item.classes} isSelected={isSelected} shape={IconShape.SQUARE} size={IconSize.SIZE_ICON_16} type={IconType.TRANSPARENT} />
          {renderLabel(item)}
        </button>
      </li>
    );
  }

  return (
    <div className={styles.wrapper}>
      <Text val={window.i18next.t(descriptor.titleKey)} size={TextSize.MD} className={styles.title} />
      <ul className={styles.list}>{descriptor.list.map((item) => renderItem(item))}</ul>
    </div>
  );
}

export function makeOptionListControl(descriptor: OptionListDescriptor): EmailMakerControl {
  return createDescriptorControl(OptionListControlInner, descriptor);
}
